import type { Transcription } from '@klangio/ai2notes-ts-client';
import AccountCircle from '@mui/icons-material/AccountCircle';
import AddCircle from '@mui/icons-material/AddCircle';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ExitToApp from '@mui/icons-material/ExitToApp';
import GroupIcon from '@mui/icons-material/Group';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Alert,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  SwipeableDrawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useContext, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AppConstantsContext, UserContext } from '../../../context';
import useUserDialog from '../../../hooks/useUserDialog';
import { TrackingAction, TrackingCategory, trackingService } from '../../../services';

import { TranscriptionState } from '../../../entities';
import useLocaleNavigate from '../../../hooks/useLocalNavigation';
import { COMPANY_URL } from '../../../utils';
import { LanguageSelector } from '../../molecules';
import PricingTableModal from '../../templates/PricingTableModal';
import UserDialog from '../Dialogs/UserDialog';

export interface UserMenuProps {
  transcription?: Transcription;
  navigateToLogin: () => void;
  navigateToRegister: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({
  transcription,
  navigateToLogin,
  navigateToRegister,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const theme = useTheme();
  const { isDialogOpen, setOpenDialog, onSaveChanges } = useUserDialog();
  const matchesSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openPricingModal, setOpenPricingModal] = useState(false);
  const { numberOfTickets, user, logout } = useContext(UserContext);
  const { title, inviteFriends, languages, isMusehub } = useContext(AppConstantsContext);
  const { navigateTo } = useLocaleNavigate();

  const handleCloseSnackbar = () => {
    setCopied(false);
  };

  const onInviteFriend = () => {
    trackingService.event(TrackingCategory.User, TrackingAction.INVITE_FRIENDS);
    t('nav.invite_friends_text', {
      title,
    });
    const message = `${t('nav.invite_friends_text', {
      title,
    })} ${inviteFriends}${user.id}`;
    if (navigator.share) {
      navigator
        .share({
          text: message,
          url: `${inviteFriends}${user.id}`,
        })
        .catch(console.error);
    } else {
      navigator.clipboard.writeText(message).then(() => {
        setCopied(true);
      });
    }
  };

  return (
    <div>
      {!isMusehub && (
        <IconButton
          edge="start"
          style={{ color: '#fff' }}
          id="btn_menu_burger"
          aria-label="menu"
          onClick={() => setDrawerOpen(!drawerOpen)}
        >
          <MenuIcon />
        </IconButton>
      )}
      {drawerOpen && (
        <SwipeableDrawer
          anchor="right"
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
        >
          <div style={{ height: '100%' }}>
            <List component="nav">
              {user && (
                <ListItem>
                  <ListItemText primary={`${numberOfTickets} ${t('nav.songs_left')}`} />
                </ListItem>
              )}
            </List>
            <List component="nav">
              <LanguageSelector listItem languages={languages} />
              {!user && matchesSmallScreen && (
                <List>
                  <ListItemButton id="btn_menu_login" onClick={navigateToLogin}>
                    <ListItemIcon>
                      <MeetingRoomIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('nav.login')} />
                  </ListItemButton>
                  <ListItemButton id="btn_menu_register" onClick={navigateToRegister}>
                    <ListItemIcon>
                      <AssignmentIndIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('nav.sign_up')} />
                  </ListItemButton>
                </List>
              )}
              {!(user?.subscriptions?.length > 0)
                && (!transcription || transcription?.state === TranscriptionState.Ok) && (
                  <ListItemButton id="btn_menu_upgrade" onClick={() => setOpenPricingModal(true)}>
                    <ListItemIcon>
                      <AddCircle />
                    </ListItemIcon>
                    <ListItemText primary={t('common.upgrade')} />
                  </ListItemButton>
              )}
              {user && (
                <>
                  <ListItemButton id="btn_menu_account" onClick={() => setOpenDialog(true)}>
                    <ListItemIcon>
                      <AccountCircle />
                    </ListItemIcon>
                    <ListItemText primary="Account Info" />
                  </ListItemButton>
                  <ListItemButton
                    id="btn_menu_songbook"
                    onClick={() => {
                      navigateTo('/my-files');
                    }}
                  >
                    <ListItemIcon>
                      <AutoStoriesIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('nav.my_files')} />
                  </ListItemButton>
                  <ListItemButton id="btn_menu_invite_friends" onClick={onInviteFriend}>
                    <ListItemIcon>
                      <GroupIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('nav.invite_friend')} />
                  </ListItemButton>
                  <ListItemButton id="btn_menu_logout" onClick={logout}>
                    <ListItemIcon>
                      <ExitToApp />
                    </ListItemIcon>
                    <ListItemText primary={t('nav.logout')} />
                  </ListItemButton>
                </>
              )}
            </List>
          </div>
          <List component="div">
            <Divider />
            <ListItemButton id="btn_menu_updates" href={`${COMPANY_URL}/updates`}>
              <ListItemText primary={t('footer.updates')} />
            </ListItemButton>
            <ListItemButton id="btn_menu_privacy" href={`${COMPANY_URL}/privacy/`}>
              <ListItemText primary={t('footer.privacy')} />
            </ListItemButton>
            <ListItemButton id="btn_menu_imprint" href={`${COMPANY_URL}/imprint/`}>
              <ListItemText primary={t('footer.imprint')} />
            </ListItemButton>
          </List>
        </SwipeableDrawer>
      )}
      {copied && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={copied}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
        >
          <Alert severity="success">{t('nav.copied_alert')}</Alert>
        </Snackbar>
      )}
      {openPricingModal && (
        <PricingTableModal
          transcription={transcription}
          handleClose={() => setOpenPricingModal(false)}
          open={openPricingModal}
        />
      )}
      {user && (
        <UserDialog
          open={isDialogOpen}
          handleClose={() => setOpenDialog(false)}
          onSave={onSaveChanges}
        />
      )}
    </div>
  );
};

export default UserMenu;
